import { Injectable, inject } from "@angular/core";
import { createAuth0Client, type Auth0Client } from "@auth0/auth0-spa-js";


import { AuthService } from "../services/auth.service";
import { AUTH_CONTEXT } from "./auth-context.token";
import { environment } from "../../../environments/environment";

import type { AccountInfo } from "@azure/msal-browser";
import type { IAuthProvider } from "./interfaces";

/**
 * Auth0 implementation of IAuthProvider for external SP users. The active
 * organization comes from AUTH_CONTEXT (the <slug> subdomain). Auth0 id-token
 * roles live under the namespaced claim (environment.auth0.rolesClaim); the
 * adapter lifts them to a top-level `roles` array so the shared AuthService —
 * and the sidebar/header userProfile — work exactly as on the AAD path.
 */
@Injectable({ providedIn: "root" })
export class Auth0AuthService implements IAuthProvider {
  private readonly authState = inject(AuthService);

  private readonly authContext = inject(AUTH_CONTEXT);

  private client: Auth0Client | null = null;

  private get organization(): string | undefined {
    return this.authContext.organization ?? undefined;
  }

  private async getClient(): Promise<Auth0Client> {
    if (this.client === null) {
      this.client = await createAuth0Client({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        authorizationParams: {
          audience: environment.auth0.audience,
          organization: this.organization,
          redirect_uri: `${window.location.origin}/`,
        },
        cacheLocation: "localstorage",
      });
    }
    return this.client;
  }

  private toAccount(claims: Record<string, unknown>): AccountInfo {
    const rolesClaim: string = environment.auth0.rolesClaim;
    const roles: unknown = claims[rolesClaim];
    return {
      idTokenClaims: {
        ...claims,
        roles: Array.isArray(roles) ? roles : [],
      },
    } as unknown as AccountInfo;
  }

  public async initialize(loginHint: string | null): Promise<void> {
    // Unlike the AAD path, Auth0 silent auth at bootstrap relies on the session
    // cookie, not a login_hint — the hint is only used in login(). Discarded here.
    void loginHint;
    const authClient = await this.getClient();
    const search: string = window.location.search;
    if (search.includes("code=") && search.includes("state=")) {
      try {
        await authClient.handleRedirectCallback();
        window.history.replaceState({}, "", window.location.pathname);
      } catch {
        // fall through to the silent attempt below
      }
    }
    try {
      await authClient.getTokenSilently();
      const claims = await authClient.getIdTokenClaims();
      if (claims) {
        this.authState.setFromAccount(this.toAccount(claims as Record<string, unknown>));
      }
    } catch {
      // unauthenticated — login screen / auth guard handles redirect
    }
  }

  public async acquireApiToken(): Promise<string | null> {
    try {
      const authClient = await this.getClient();
      return await authClient.getTokenSilently();
    } catch {
      return null;
    }
  }

  public async login(loginHint: string | null): Promise<void> {
    const authClient = await this.getClient();
    await authClient.loginWithRedirect({
      authorizationParams: {
        organization: this.organization,
        audience: environment.auth0.audience,
        redirect_uri: `${window.location.origin}/`,
        login_hint: loginHint ?? undefined,
      },
    });
  }

  public async logout(): Promise<void> {
    this.authState.clearToken();
    const authClient = await this.getClient();
    await authClient.logout({
      logoutParams: { returnTo: `${window.location.origin}/login` },
    });
  }
}
